/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import { useState } from 'react';
import { BodyText, Button, ButtonType, Checkbox, DatePicker, Form, FormItem, Heading, Icon, InputField, List, ListItem, NavTabs, NotificationType, PageContainer, Paper, Popover, Size, Spinner, Stack, Tooltip } from '@vaisala/rockhopper-components';
import { useProject } from '../state/project';
import styled from 'styled-components';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { ReconstructionStatus } from '../components/ReconstructionStatus';
import { DataStatus } from './DataManagementPage';
import { useModal } from '../state/modal';

import { useNotification } from '../state/notifications';
import { useAuth } from '../state/auth/AuthContext';

const WindReconstructionContainer = styled(Paper)`

  position: relative;
  min-width: 1000px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  
  .main-stack {
    position: relative;
    width: 100%;
    height: 70%;
    margin-top: 30px;
    margin-bottom: 15px;
  }  
  
  .form {
    min-width: 340px;
    width: 39%;
    height: 100%;
    overflow-y: auto;
  }

  .timestamps {
    width: 100%;
    padding-right: 100px;
  }

  .time-pick {
    margin-top: 0;
  }

  .progress-box {
    position: relative;
    height: 23%;
    width: 100%;
    display : flex;
    flex-direction : row;
    align-items : end;
    justify-content : space-between;
    bottom: 0px;
  }
  
  .files_container {
    width: 59%;
    height: 100%;
    overflow-y: auto;
  }
  
  .tab {
    width: 100%;
  }

  .reconstruct-button {
    position : relative;
    right: 0px;
    bottom: 0px;
    margin-right: 25px;
    height : 50px;
  }

`;

const WindReconstructionPage : React.FC = () => {
  const {secureFetch} = useAuth()

  const {
    project, processCount,
    ncDataHistory, uploadInfo, 
    reconstructionSettings, updateReconstructionSettings,
    reconstructionInfo, setReconstructionInfo,
    setSelectedVmms, setOnefile,
    reconstructedFiles, errorFiles, fetchReconstruction } = useProject();
  
  const { notify } = useNotification();

  const { modals } = useModal();

  const [dataStatus, setDataStatus] = useState<DataStatus>(ncDataHistory.dates.length > 0 ? "DATA" : "NO_DATA")

  const [disable, setDisable] = useState<boolean>(false);

  const [startDate, setStartDate] = useState<string>(ncDataHistory.dates.length > 0 ? ncDataHistory.dates[0] + "T00:00:00" : (new Date()).toISOString());
  const [endDate, setEndDate] = useState<string>(ncDataHistory.dates.length > 0 ? ncDataHistory.dates[ncDataHistory.dates.length - 1] + "T23:59:59" : (new Date()).toISOString());

  const [datesList, setDatesList] = useState<string[] | undefined>(ncDataHistory.dates.length > 0 ? ncDataHistory.dates : undefined);
  const [daysToReconstruct, setDaysToReconstruct] = useState<string[]>(ncDataHistory.dates.length > 0 ? ncDataHistory.dates : []);

  const [vmms] = useState<string[]>(project.details?.vmms??[]);
  const [newSelectedVmms, setNewSelectedVmms] = useState<string[]>(project.details?.vmms??[]);
  const [newOnefile, setNewOnefile] = useState(true);
  const [frequency, setFrequency] = useState<string>(reconstructionSettings.frequency??"00:10:00");

  const [displayErrors, setDisplayErrors] = React.useState<boolean>(false);
  const [selectedVmmsValid, setSelectedVmmsValid] = React.useState<boolean>(true);
  const [frequencyValid, setFrequencyValid] = React.useState<boolean>(true);

  const [selectedTabKey, setSelectedTabKey] = React.useState('1');

  const generateDateList = (startDateISO: string, endDateISO: string): string[] => {
    let newstartDate = new Date(startDateISO);
    let newendDate = new Date(endDateISO);

    newstartDate = new Date(newstartDate.getTime()  - (newstartDate.getTimezoneOffset() * 60 * 1000))
    newendDate = new Date(newendDate.getTime()  - (newendDate.getTimezoneOffset() * 60 * 1000))

    const dateList: string[] = [];
  
    if (newstartDate > newendDate) {
      return dateList; // Return an empty list if start date is after end date
    }
  
    let currentDate = newstartDate;
  
    while (currentDate <= newendDate) {
      if (uploadInfo.status !== "RUNNING" || uploadInfo.project !== project.name || !uploadInfo.dates.includes(currentDate.toISOString().split('T')[0]))
        dateList.push(currentDate.toISOString().split('T')[0]); // Add the date as an ISO string without time
      currentDate.setDate(currentDate.getDate() + 1); // Increment to the next day
    }
  
    return dateList;
  }

  React.useEffect(() => {

    if (reconstructionInfo.status !== "RUNNING"){
      const newDateListToReconstruct = generateDateList(startDate, endDate);
      setDaysToReconstruct(newDateListToReconstruct);
    }

  }, [startDate, endDate]);

  React.useEffect(() => {

    if( reconstructionInfo.status !== 'RUNNING'){

      if (ncDataHistory.dates.length > 0){
        let available_dates = ncDataHistory.dates.filter((it : string) => !(uploadInfo.dates.includes(it) && uploadInfo.status === "RUNNING" && uploadInfo.project === project.name));        
        if (available_dates.length > 0){
          setDatesList(available_dates);

          if (daysToReconstruct.length === 0){
            setStartDate(available_dates[0] + "T00:00:00");
            setEndDate(available_dates[available_dates.length-1] + "T23:59:59");
          }

          setDataStatus("DATA");
          if (reconstructionInfo.status !== "ERROR")
            setReconstructionInfo({...reconstructionInfo, status : "READY"})

        }
      } else {
        setDataStatus("NO_DATA");
      }
    }

  }, [ncDataHistory.dates]);

  const recoverFiles = async () => {
    setDisable(true);
    fetchReconstruction('load');
    setSelectedTabKey("1");
    setDisable(false);
  }

  React.useEffect(() => {
    if(reconstructionInfo.status === 'START' && dataStatus === "DATA"){
      setDisable(true);
    }
    else if (reconstructionInfo.status === 'FINISHED'){
      setDataStatus("DATA_UPLOADED");
      handleFinished();
    } else if (reconstructionInfo.status === 'STOP'){
      handleFinished();
    }
    else if (reconstructionInfo.status === 'ERROR'){
      handleFinished();
    }

    }, [reconstructionInfo.status]);

  const handleFinished = () => {
    setSelectedTabKey("1");
    setDisable(false);
  }

  const handleVmmSelection = (vmmId: string) => {

    const updatedSelection = newSelectedVmms.includes(vmmId)
      ? newSelectedVmms.filter((vmm) => vmm !== vmmId)
      : [...newSelectedVmms, vmmId];

    if ([...updatedSelection].length === 0){
      setSelectedVmmsValid(false);
    }
    else {
      setSelectedVmmsValid(true);
    }
    setNewSelectedVmms([...updatedSelection]);
  };

  const validateFrequency = () => {

    if (!frequency.includes(":")){

      setFrequencyValid(false);

    } else {

    if (frequency.split(":").length === 3){
      let check_num : boolean[] = frequency.split(":").map((value, index) => {
        if (index === 0){
          return value.length === 2 && Number.isSafeInteger(Number(value)) && parseInt(value) >= 0 && parseInt(value) <= 23;
        }
        else{
          return value.length === 2 && Number.isSafeInteger(Number(value)) && parseInt(value) >= 0 && parseInt(value) <= 59;
        }
      });

      setFrequencyValid(check_num.every((v) => v));

    }
    else {
      setFrequencyValid(false);
    }
    }
  }

  const handleDownload = async (filename: string) => {

    if (filename === "all_nc" && reconstructedFiles){

      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction/download/' , {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'files' : reconstructedFiles,
            'errors' : false
          })
        });

        const file_data = await response.blob();
        
        const url = window.URL.createObjectURL(file_data);
        const a = document.createElement('a');
        a.href = url;
        a.download = "reconstruction_ncfiles.zip";
        a.click();
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Error downloading nc files : ', error);
      }

    }
    else if (filename === "all_logs" && errorFiles) {

      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction/download/' , {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'files' : errorFiles,
            'errors' : true
          })
        });

        const file_data = await response.blob();
        
        const url = window.URL.createObjectURL(file_data);
        const a = document.createElement('a');
        a.href = url;
        a.download = "reconstruction_errors.zip";
        a.click();
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Error downloading error logs : ', error);
      }

    }
    else {
      
      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction/download/' , {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'files' : [filename]
          })
        });

        const file_data = await response.blob();
        
        const url = window.URL.createObjectURL(file_data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Error while downloading files:', error);
      }

    }
  }

  const handleStopProcess = () => {
    if (reconstructionInfo.status === "RUNNING")
      setReconstructionInfo({...reconstructionInfo, status: "STOP"})
  }

  return (
    <PageContainer.Content>
      <WindReconstructionContainer>
        <Stack direction='row' justify={'space-between'} spacing={Size.None} className='main-stack'>
          <Paper className='form'>
            <Form dataTa="add-user-form">
              <FormItem  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} label="Start Date : ">
                <DatePicker
                  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"}
                  value={new Date(startDate)}
                  dateFormat={"dd/MM/yyyy"}
                  onChange={(date : any)=> {
                    date.setTime(date.getTime() - date.getTimezoneOffset()*60*1000);
                    let newDateString : string = date.toISOString().split("T")[0] + "T" + startDate.split("T")[1];
                    if (new Date(newDateString).getTime() > new Date(endDate).getTime()){
                      setStartDate(endDate);
                      setEndDate(newDateString);
                    } else {
                      setStartDate(newDateString);
                    }
                  }}
                  startDate={new Date(startDate)}
                  endDate={new Date(endDate)}
                  config={{
                      selectsStart: true,
                      filterDate: (d : Date) => {
                        let index = datesList?.findIndex( dt => (new Date(dt+"T00:00:00")).getTime() === d.getTime());
                        return (index??0) >= 0;
                      }
                  }}/>
              </FormItem>

              <FormItem  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} label="End Date : ">
                <DatePicker
                  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"}
                  value={new Date(endDate)}
                  dateFormat={"dd/MM/yyyy"}
                  onChange={(date: any) => {
                    date.setTime(date.getTime() - date.getTimezoneOffset()*60*1000);
                    let newDateString : string = date.toISOString().split("T")[0] + "T" + endDate.split("T")[1];
                    if (new Date(newDateString).getTime() < new Date(startDate).getTime()){
                      setEndDate(startDate);
                      setStartDate(newDateString);
                    } else {
                      setEndDate(newDateString);
                    }
                  }}
                  startDate={new Date(startDate)}
                  endDate={new Date(endDate)}
                  config={{
                      selectsStart: true,
                      filterDate: (d : Date) => {
                        let index = datesList?.findIndex( dt => (new Date(dt+"T00:00:00")).getTime() === d.getTime());
                        return (index??0) >= 0;
                      }
                    }
                    }
                />
              </FormItem>

              <FormItem disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} label="VMM(s) : " errors={(displayErrors && !selectedVmmsValid) ? "Select at least one VMM":false}>
                {vmms.map((vmm) => (
                  <div>
                    <Checkbox 
                      disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"}
                      key={vmm}
                      id={`vmm-${vmm}`}
                      label={"  " + vmm}
                      checked={newSelectedVmms.includes(vmm)}
                      onChange={() => {
                        handleVmmSelection(vmm);
                      }}>
                      
                    </Checkbox>
                  </div>
                ))}
              </FormItem>

              <FormItem  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} label="Frequency of STA :" errors={(displayErrors && !frequencyValid) ? "Use format 'HH:MM:SS' only.":false}>
                  <InputField
                    disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"}
                    autoComplete="off"
                    dataTa="campaign_name"
                    name="campaignName"
                    value={frequency}
                    onChange={e =>
                        {
                            setFrequency(e.target.value);
                        }
                    }
                    onBlur={validateFrequency}
                    width={Size.XS}
                  />
              </FormItem>

              <FormItem  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} label="Group VMM in one file : ">
                <Checkbox
                  disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"}
                  width={Size.L} placeholder="Choose" dataTa="onefile"
                  onChange={() => setNewOnefile(!newOnefile)}
                  checked={newOnefile}
                  />
              </FormItem>
            </Form>
            <Tooltip
              arrow
              triggerElement={
                <Button disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable} buttonSize={Size.S} onClick = {modals.reconstruction_params} >
                  <Icon name={VaiIcon.Settings}/>
                </Button>
              }
            >
              Advanced Reconstruction Settings
            </Tooltip>
          </Paper>

          <Paper title='Wind files to download' className='files_container'>
            <Stack direction='row' justify='space-between'>
              <Heading level = {3}>Wind files</Heading>
              <Stack style={{'width':'50%'}}direction='row' justify='end'>
                <Tooltip
                arrow
                placement='top'
                triggerElement={
                  <Button disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} buttonSize={Size.S} onClick={recoverFiles}>
                    <Icon name={VaiIcon.Refresh}/>
                  </Button>
                  }
                >
                  Recover files from last reconstruction
                </Tooltip>
                {
                  selectedTabKey === "1" ?
                  <Button id="downloadButton" disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA" || reconstructedFiles.length <= 1} buttonSize={Size.S} onClick = {() => handleDownload("all_nc")} >
                    Download NC Files
                  </Button>:
                  <Button id="downloadButton" disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA" || errorFiles.length <= 1} buttonSize={Size.S} onClick = {() => handleDownload("all_logs")} >
                    Download Log Files
                  </Button>
                }
              </Stack>
            </Stack>
            { reconstructionInfo.status === "LOADING" ?
              <Spinner />:
              <NavTabs
                  className='tab'
                  htmlId={['my nav tabs']}
                  selectedTabKey={selectedTabKey}
                  setSelectedTabKey={setSelectedTabKey}
                  onSelectedTabChanged={selectedTabKey => setSelectedTabKey(selectedTabKey)}
              >
                <NavTabs.Tab tabKey={"1"} title={"NetCDF Files"}>
                    <List>
                      {reconstructedFiles?.map((file, index) => {return(
                        <ListItem>
                          <Icon size={Size.L} name={VaiIcon.DocumentExcel}/>
                          {file}
                          <Button disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} buttonType={ButtonType.Secondary} buttonSize={Size.S} onClick={() => handleDownload(file)}>
                            <Icon name={VaiIcon.Download}/>
                          </Button>
                        </ListItem>)}
                      )
                      }
                    </List>      
                </NavTabs.Tab>
                <NavTabs.Tab tabKey={"2"} title={"Error logs"}>
                    <List>
                      {errorFiles?.map((file, index) => {return(
                        <ListItem>
                          <Icon size={Size.L} name={VaiIcon.DocumentExcel}/>
                          {file}
                          <Button disabled={reconstructionInfo.status === "RUNNING" || reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"} buttonType={ButtonType.Secondary} buttonSize={Size.S} onClick={() => handleDownload(file)}>
                            <Icon name={VaiIcon.Download}/>
                          </Button>
                        </ListItem>)}
                      )
                      }
                    </List>        
                </NavTabs.Tab>
              </NavTabs>
            }        
          </Paper>
        </Stack>
        <div className='progress-box'>
          <ReconstructionStatus dataStatus={dataStatus} datesToReconstruct={daysToReconstruct}/>
          {
            (reconstructionInfo.status === 'RUNNING') ?
            <Popover
                arrow={true}
                showCloseIcon={false}
                triggerElement={
                    <Button style={{color : 'red'}} buttonType={ButtonType.Secondary} buttonSize={Size.S}>
                        <Icon name={VaiIcon.Stop}/>
                    </Button>
                }
            >
                <BodyText style={{marginBottom: '2px'}}>
                Are you sure?
                </BodyText>
                <Button style={{color : 'red'}} buttonType={ButtonType.Secondary}  onClick={() => handleStopProcess()}>
                    Stop
                </Button>
            </Popover> :
            <Button
              disabled={reconstructionInfo.status === "LOADING" || reconstructionInfo.status === "STOP" || disable || dataStatus === "NO_DATA"}
              buttonType={ButtonType.Primary}
              onClick={async () => {
                if (processCount + 1 <= 3){
                  if (selectedVmmsValid && frequencyValid){
                    if ( dataStatus === "DATA_UPLOADED" )
                        setDataStatus("DATA");
                    setDisplayErrors(false);
                    if (frequency !== reconstructionSettings.frequency){
                      localStorage.setItem("reconstruction_configuration_changed", "true");
                      await updateReconstructionSettings({'frequency' : frequency})
                    }
                    setSelectedVmms(newSelectedVmms)
                    setOnefile(newOnefile);
                    setReconstructionInfo({status : "START", dates : daysToReconstruct, files_reconstructed : 0});
                  } else {
                    setDisplayErrors(true);
                  }                  
                } else {
                  notify({
                    type: NotificationType.Info,
                    content: "Too many process running. Wait for a process to finish to launch this reconstruction",
                    duration: 3000});
                }
              }}
              className='reconstruct-button'>
              Launch Reconstruction
            </Button>
          }
        </div>

      </WindReconstructionContainer>
    </PageContainer.Content>
  );
}

export default WindReconstructionPage;